import React, { FC } from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Divider,
    Flex,
    Heading,
    Link,
    LinkProps,
    Text
} from '@chakra-ui/react';
import CircleIcon from 'ts/common/components/gallery/CircleIcon';
import { BundleFileIcon, DownloadIcon } from 'ts/common/components/icons';

const HelpfulTipsButton: FC<ButtonProps & LinkProps> = ({ children, ...rest }) => (
    <Button
        as={Link}
        target="_blank"
        rel="noopener noreferrer"
        borderColor="gray.400"
        color="text"
        fontWeight="semibold"
        variant="outline"
        width={{ base: 'full', lg: '358px' }}
        _active={{
            backgroundColor: 'initial'
        }}
        _focus={{
            boxShadow: 'none'
        }}
        _focusVisible={{
            boxShadow: 'outline'
        }}
        _hover={{
            backgroundColor: 'initial',
            textDecoration: 'none'
        }}
        _visited={{
            color: 'text'
        }}
        {...rest}
    >
        {children}
    </Button>
);

export interface IBundleFile {
    filename: string;
    size: string;
}

export interface IBundleVideo {
    filename: string;
    url: string;
}

export const getFileLabel = (file: IBundleFile) => `${file.filename} (${file.size})`;

interface Props {
    /** A list of files to be rendered */
    files: IBundleFile[];
    /** A list of videos to be rendered. Videos are not zipped; they are direct download links. */
    videos?: IBundleVideo[];
    /** A function to be called when a file or video is clicked */
    onClickFile: (file: IBundleFile | IBundleVideo) => unknown;
    /** The Angular translation service */
    translateFilter: SpAngularJs.ITranslateFilter;
}

const BundleFileList: FC<Props> = ({
    files,
    videos = [],
    onClickFile,
    translateFilter: translate
}) => {
    const renderItem = (
        item: IBundleFile | IBundleVideo,
        key: string,
        ariaLabel: string,
        icon: typeof BundleFileIcon,
        size?: string
    ) => (
        <Button
            key={key}
            onClick={() => onClickFile(item)}
            aria-label={ariaLabel}
            alignItems="center"
            borderRadius="0px"
            borderStyle="none"
            color="brand.primary"
            display="flex"
            flexDirection="column"
            textDecoration="underline"
            variant="link"
            width="120px"
            _focus={{
                boxShadow: 'none'
            }}
            _focusVisible={{
                boxShadow: 'outline'
            }}
        >
            <CircleIcon icon={icon} size="lg" />
            <Text
                color="brand.primary"
                fontSize="md"
                fontWeight="semibold"
                isTruncated
                letterSpacing="0px"
                marginTop="8px"
                maxWidth="120px"
                title={item.filename}
            >
                {item.filename}
            </Text>
            {/* Always reserve the size line's height (videos have no size) so
                items stay vertically aligned within a row. */}
            <Text color="brand.primary" minHeight="1.5em">
                {size}
            </Text>
        </Button>
    );

    return (
        <Flex alignItems="center" flexDirection="column">
            <Flex alignItems="center" flexFlow="row wrap" gap="48px 64px" justifyContent="center">
                {files.map((file) =>
                    renderItem(file, file.filename, getFileLabel(file), BundleFileIcon, file.size)
                )}
                {videos.map((video) => renderItem(video, video.url, video.filename, DownloadIcon))}
            </Flex>
            <Divider marginY="32px" />
            <Box marginBottom="24px">
                <Heading as="h3" marginY="16px" size="md" variant="primary">
                    {translate('zipBundleHelpfulTips')}
                </Heading>
                <Text fontSize="md" marginY="24px">
                    {translate('zipBundleHelpfulTipsItem1')}
                </Text>
                <Text fontSize="md" marginY="24px">
                    {translate('zipBundleHelpfulTipsItem2')}
                </Text>
            </Box>
            <Flex alignItems="center" alignSelf="stretch" flexDirection="column" gap="16px">
                <HelpfulTipsButton href="https://support.apple.com/kb/PH25411">
                    macOS
                </HelpfulTipsButton>
                <HelpfulTipsButton href="https://support.microsoft.com/help/14200/windows-compress-uncompress-zip-files">
                    Windows
                </HelpfulTipsButton>
            </Flex>
        </Flex>
    );
};

export default BundleFileList;
